import { ChevronDown, ChevronRight, Volume2 } from "lucide-react";

import type {
  AudioSettingsState,
  SoundType,
} from "~/components/stations/station-edit-types";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "~/components/ui/collapsible";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Slider } from "~/components/ui/slider";
import { Switch } from "~/components/ui/switch";

interface KdsAudioSettingsSectionProps {
  audioSettings: AudioSettingsState;
  setAudioSettings: (settings: AudioSettingsState) => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

export function KdsAudioSettingsSection({
  audioSettings,
  setAudioSettings,
  isOpen,
  setIsOpen,
}: KdsAudioSettingsSectionProps) {
  return (
    <Collapsible onOpenChange={setIsOpen} open={isOpen}>
      <CollapsibleTrigger asChild>
        <div className="border-border hover:bg-accent/50 flex cursor-pointer items-center justify-between rounded-xl border px-4 py-3">
          <div className="flex items-center gap-3">
            <Volume2 className="text-muted-foreground h-5 w-5" />
            <div>
              <p className="font-medium">Audio Settings</p>
              <p className="text-muted-foreground text-sm">
                Sound alerts for new orders
              </p>
            </div>
          </div>
          {isOpen ? (
            <ChevronDown className="text-muted-foreground h-5 w-5" />
          ) : (
            <ChevronRight className="text-muted-foreground h-5 w-5" />
          )}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="border-border bg-accent/30 mt-2 space-y-4 rounded-xl border p-4">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <Label>Volume</Label>
              <span className="text-muted-foreground text-sm">
                {audioSettings.volume}%
              </span>
            </div>
            <Slider
              max={100}
              min={0}
              onValueChange={(values) =>
                setAudioSettings({ ...audioSettings, volume: values[0] ?? 80 })
              }
              step={5}
              value={[audioSettings.volume]}
            />
          </div>

          <div className="space-y-3">
            <SoundOption
              enabled={audioSettings.newOrderSound}
              label="New Order Sound"
              onEnabledChange={(v) =>
                setAudioSettings({ ...audioSettings, newOrderSound: v })
              }
              onSoundTypeChange={(v) =>
                setAudioSettings({ ...audioSettings, newOrderSoundType: v })
              }
              soundType={audioSettings.newOrderSoundType}
            />
            <SoundOption
              enabled={audioSettings.prioritySound}
              label="Priority Sound"
              onEnabledChange={(v) =>
                setAudioSettings({ ...audioSettings, prioritySound: v })
              }
              onSoundTypeChange={(v) =>
                setAudioSettings({ ...audioSettings, prioritySoundType: v })
              }
              soundType={audioSettings.prioritySoundType}
            />
            <SoundOption
              enabled={audioSettings.customerArrivedSound}
              label="Customer Arrived"
              onEnabledChange={(v) =>
                setAudioSettings({ ...audioSettings, customerArrivedSound: v })
              }
              onSoundTypeChange={(v) =>
                setAudioSettings({
                  ...audioSettings,
                  customerArrivedSoundType: v,
                })
              }
              soundType={audioSettings.customerArrivedSoundType}
            />
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function SoundOption({
  label,
  enabled,
  soundType,
  onEnabledChange,
  onSoundTypeChange,
}: {
  label: string;
  enabled: boolean;
  soundType: SoundType;
  onEnabledChange: (enabled: boolean) => void;
  onSoundTypeChange: (type: SoundType) => void;
}) {
  return (
    <div className="bg-background flex items-center justify-between rounded-lg px-3 py-2">
      <div className="flex items-center gap-3">
        <Switch checked={enabled} onCheckedChange={onEnabledChange} />
        <span className="text-sm">{label}</span>
      </div>
      {enabled && (
        <Select
          onValueChange={(value) => {
            if (
              value === "chime" ||
              value === "bell" ||
              value === "alert" ||
              value === "ding" ||
              value === "notification" ||
              value === "none"
            ) {
              onSoundTypeChange(value);
            }
          }}
          value={soundType}
        >
          <SelectTrigger className="h-8 w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="chime">Chime</SelectItem>
            <SelectItem value="bell">Bell</SelectItem>
            <SelectItem value="alert">Alert</SelectItem>
            <SelectItem value="ding">Ding</SelectItem>
            <SelectItem value="notification">Notification</SelectItem>
            <SelectItem value="none">None</SelectItem>
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
